'use client';

import * as React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useDebouncedValue } from '../hooks/use-debounce';
import { useIsMobile } from '../hooks/use-mobile';
import type { ResolvedAutocompleteProps } from './autocomplete-types';

/**
 * State + behavior for `Autocomplete`. Extracted from `autocomplete.tsx`
 * (conformance, code-size guard). Owns open/highlight/input state, the derived
 * `filteredOptions` / `canCreate` / `displayValue`, keyboard + selection
 * handlers, and the ARIA ids consumed by the desktop and mobile views.
 */
export function useAutocomplete(props: ResolvedAutocompleteProps) {
  const {
    value,
    onChange,
    options,
    label,
    multiple,
    maxSelections,
    creatable,
    onCreate,
    minChars,
    debounceMs,
    onInputChange,
    serverFiltered,
    clearOnSelect,
    autoSelectOnBlur,
    selectOnFocus,
    id,
  } = props;

  // ============================================================================
  // State & Refs
  // ============================================================================
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isCreating, setIsCreating] = useState(false);
  // True only while the input holds text the user typed since the last pick.
  // After a pick, `inputValue` keeps the option label; that echo must never be
  // re-settled, or a later programmatic `value` change (a form reset, a country
  // derived from an address) would be overwritten on the next click.
  const [hasTyped, setHasTyped] = useState(false);
  // False from a pick until the parent next changes `value`. Meanwhile the
  // field shows the pick (`inputValue`), even while an async consumer is still
  // resolving it. Any later `value` change (the resolved pick, a form reset, a
  // derived field) makes the field follow `value` again.
  const [followsValue, setFollowsValue] = useState(true);
  // The last single-select pick. When `value` still names it but `options` no
  // longer carry it (a server search re-run on the picked label returned
  // nothing), its label is still the right thing to show.
  const [lastPick, setLastPick] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    // Adjust state on a prop change during render, not in an effect.
    setPrevValue(value);
    if (!followsValue) setFollowsValue(true);
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const debouncedInputValue = useDebouncedValue(inputValue, debounceMs);

  // Generate unique IDs for ARIA
  const baseId = useId();
  // A form wrapper's id wins so its label and message reach the real input.
  const inputId = id ?? `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const labelId = label ? `${baseId}-label` : undefined;
  const statusId = `${baseId}-status`;

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Typed text that never became a selection must not outlive the dropdown.
  // Otherwise the field paints "Germany" while the form value stays empty, and
  // a required-field gate refuses a field the user can see is filled (contract
  // wizard, 2026-10-05). Scope: single-select fields that hold a value. Out of
  // scope: creatable (unmatched text may be a new entry, left to `onCreate`)
  // and `clearOnSelect` (a search box whose pick navigates, not a value).
  const settlesTypedText = !multiple && !creatable && !clearOnSelect;
  // Deleting all the text clears the value for any single-select that holds
  // one, creatable included (React Aria `allowsCustomValue` does the same).
  // Without it a creatable field looked empty while still holding its value.
  const clearsOnEmpty = !multiple && !clearOnSelect;

  // Get selected values as array
  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter options based on input. When `serverFiltered` is set, the options
  // are already the authoritative server result — skip the client-side
  // substring filter so server matches aren't hidden mid-keystroke.
  const filteredOptions = React.useMemo(() => {
    if (serverFiltered || !inputValue || inputValue.length < minChars) {
      return options;
    }
    const searchTerm = inputValue.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchTerm) ||
        opt.description?.toLowerCase().includes(searchTerm),
    );
  }, [options, inputValue, minChars, serverFiltered]);

  // Check if we can create a new option
  const canCreate = React.useMemo(() => {
    if (!creatable || !inputValue.trim()) return false;
    const normalizedInput = inputValue.trim().toLowerCase();
    return !options.some((opt) => opt.label.toLowerCase() === normalizedInput);
  }, [creatable, inputValue, options]);

  // Get the display value for the input. In `multiple` mode the input is the
  // type-to-filter field and the selected set is surfaced separately (e.g. a
  // badge strip), so we never echo the joined selection back into it — that
  // would overflow the single-line field and duplicate the external display.
  const displayValue = React.useMemo(() => {
    // While the user is editing a single-select field, show exactly what they
    // typed, empty included. Falling back to the selected label here is what
    // made a cleared field snap back to its old value (2026-10-06).
    if (!multiple && hasTyped) return inputValue;
    // Untyped single-select following `value`: show it, never the stale label
    // of an earlier pick. Empty or unknown shows nothing rather than a label
    // the form does not hold, unless `value` is still the last pick.
    if (!multiple && !hasTyped && followsValue) {
      if (typeof value !== 'string' || !value) return '';
      const label = options.find((o) => o.value === value)?.label;
      if (label) return label;
      return lastPick?.value === value ? lastPick.label : '';
    }
    if (inputValue) return inputValue;
    if (multiple) return '';
    if (!value) return '';
    if (typeof value === 'string') {
      return options.find((o) => o.value === value)?.label || '';
    }
    return '';
  }, [value, inputValue, multiple, options, hasTyped, followsValue, lastPick]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Call onInputChange with debounced value
  useEffect(() => {
    if (onInputChange && debouncedInputValue) {
      onInputChange(debouncedInputValue);
    }
  }, [debouncedInputValue, onInputChange]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const optionEls = listRef.current.querySelectorAll('[role="option"]');
      const highlighted = optionEls[highlightedIndex];
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setHasTyped(true);
      setHighlightedIndex(-1);
      // Deleting all the text clears the value right away (React Aria): the
      // field must never look empty while the form still holds the old value.
      if (clearsOnEmpty && newValue === '' && selectedValues.length > 0) {
        onChange(null);
      }

      if (!isOpen && newValue.length >= minChars) {
        setIsOpen(true);
      }
    },
    [isOpen, minChars, clearsOnEmpty, selectedValues.length, onChange],
  );

  // At the selection cap (multiple + maxSelections): adds are ignored, removals
  // still allowed. Views use this to render the "select up to N" hint.
  const atLimit = React.useMemo(
    () =>
      !!multiple && !!maxSelections && selectedValues.length >= maxSelections,
    [multiple, maxSelections, selectedValues.length],
  );

  const handleSelect = useCallback(
    async (optionValue: string) => {
      if (multiple) {
        const isSelected = selectedValues.includes(optionValue);
        // Enforce the cap: ignore a NEW add once at the limit.
        if (
          !isSelected &&
          maxSelections &&
          selectedValues.length >= maxSelections
        ) {
          return;
        }
        const newValue = isSelected
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onChange(newValue.length > 0 ? newValue : null);
      } else {
        onChange(optionValue);
        setIsOpen(false);
      }

      if (clearOnSelect) {
        setInputValue('');
      } else if (!multiple) {
        const selectedOption = options.find((o) => o.value === optionValue);
        setInputValue(selectedOption?.label || '');
        setLastPick(
          selectedOption
            ? { value: optionValue, label: selectedOption.label }
            : null,
        );
        // Re-picking the value already held changes nothing upstream, so
        // `value` will not move: keep following it rather than freeze.
        if (optionValue !== value) setFollowsValue(false);
      }

      setHasTyped(false);
      setHighlightedIndex(-1);
    },
    [
      multiple,
      maxSelections,
      selectedValues,
      onChange,
      clearOnSelect,
      options,
      value,
    ],
  );

  const handleCreate = useCallback(async () => {
    if (!creatable || !onCreate || !inputValue.trim()) return;

    setIsCreating(true);
    try {
      const newOption = await onCreate(inputValue.trim());
      await handleSelect(newOption.value);
    } finally {
      setIsCreating(false);
    }
  }, [creatable, onCreate, inputValue, handleSelect]);

  const handleClear = useCallback(() => {
    onChange(null);
    setInputValue('');
    setHasTyped(false);
    inputRef.current?.focus();
  }, [onChange]);

  // Prefix matching needs the complete option set. Server-driven options are
  // one page of one query, possibly the previous one while the next is in
  // flight, so a prefix that looks unique there may not be.
  const optionsAreComplete = !serverFiltered && !onInputChange;

  // The one enabled option the text names: an exact label or value, the bare
  // name of a "Name (CODE)" label, else a label it is the unique prefix of.
  const findNamedOption = useCallback(
    (text: string) => {
      const needle = text.trim().toLowerCase();
      if (!needle) return null;
      const enabled = options.filter((o) => !o.disabled);
      const exact = enabled.filter((o) => {
        const label = o.label.toLowerCase();
        return (
          label === needle ||
          o.value.toLowerCase() === needle ||
          label.replace(/\s*\([^)]*\)$/, '') === needle
        );
      });
      if (exact.length === 1) return exact[0]!;
      if (exact.length > 1 || !optionsAreComplete) return null;
      const prefixed = enabled.filter((o) =>
        o.label.toLowerCase().startsWith(needle),
      );
      return prefixed.length === 1 ? prefixed[0]! : null;
    },
    [options, optionsAreComplete],
  );

  const selectedLabel = React.useMemo(() => {
    if (typeof value !== 'string' || !value) return '';
    return options.find((o) => o.value === value)?.label ?? '';
  }, [value, options]);

  // Escape, and blur without `autoSelectOnBlur`: drop the typed text and show
  // the selected value again (or nothing), never commit it.
  const discardTypedText = useCallback(() => {
    if (!settlesTypedText || !hasTyped) return;
    const held =
      selectedLabel ||
      (lastPick && lastPick.value === value ? lastPick.label : '');
    setInputValue(held);
    setHasTyped(false);
  }, [settlesTypedText, hasTyped, selectedLabel, lastPick, value]);

  // Run when the list closes without a click on an option (outside click, Tab,
  // sheet dismiss). Default: revert, as WAI-ARIA / React Aria / MUI do. With
  // `autoSelectOnBlur`: commit the one option the text names, else revert.
  const settleTypedText = useCallback(() => {
    if (!settlesTypedText || !hasTyped) return;
    const named = autoSelectOnBlur ? findNamedOption(inputValue) : null;
    if (named) {
      void handleSelect(named.value);
    } else {
      discardTypedText();
    }
  }, [
    settlesTypedText,
    hasTyped,
    autoSelectOnBlur,
    inputValue,
    findNamedOption,
    handleSelect,
    discardTypedText,
  ]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        settleTypedText();
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    // Also listen while typed text is pending with the list closed (input
    // shorter than `minChars`), so that text is settled too.
    if ((isOpen || (settlesTypedText && hasTyped)) && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, settlesTypedText, hasTyped, isMobile, settleTypedText]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const optionsCount = filteredOptions.length + (canCreate ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) =>
              prev < optionsCount - 1 ? prev + 1 : 0,
            );
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : optionsCount - 1,
            );
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            if (highlightedIndex < filteredOptions.length) {
              const option = filteredOptions[highlightedIndex];
              if (option && !option.disabled) {
                handleSelect(option.value);
              }
            } else if (canCreate) {
              handleCreate();
            }
          } else if (isOpen && settlesTypedText && autoSelectOnBlur) {
            // Nothing highlighted, auto-select mode: commit the option the text
            // names; leave ambiguous text for the user to refine. Without
            // `autoSelectOnBlur`, Enter with nothing highlighted does nothing.
            const named = findNamedOption(inputValue);
            if (named) void handleSelect(named.value);
          } else if (!isOpen) {
            setIsOpen(true);
          }
          break;

        case 'Escape':
          e.preventDefault();
          // Escape cancels: discard typed text, never commit it.
          discardTypedText();
          setIsOpen(false);
          setHighlightedIndex(-1);
          inputRef.current?.blur();
          break;

        case 'Tab':
          settleTypedText();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        case 'Backspace':
          // Chip-field affordance: backspace on an empty input removes the
          // last selected chip (standard tag-input behavior).
          if (multiple && !inputValue && selectedValues.length > 0) {
            handleSelect(selectedValues[selectedValues.length - 1]!);
          }
          break;
      }
    },
    [
      isOpen,
      highlightedIndex,
      filteredOptions,
      canCreate,
      handleSelect,
      handleCreate,
      multiple,
      inputValue,
      selectedValues,
      settlesTypedText,
      autoSelectOnBlur,
      findNamedOption,
      settleTypedText,
      discardTypedText,
    ],
  );

  const handleInputFocus = useCallback(() => {
    if (inputValue.length >= minChars || options.length > 0) {
      setIsOpen(true);
    }
    // Select the held label so the first keystroke replaces it (MUI
    // `selectOnFocus`), instead of appending to "Switzerland (CH)".
    if (selectOnFocus && !multiple) {
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [inputValue.length, minChars, options.length, selectOnFocus, multiple]);

  // Open from a collapsed summary (renderSelected) and put the caret in the
  // input, so the user can type straight away.
  const openAndFocus = useCallback(() => {
    setIsOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  return {
    isOpen,
    setIsOpen,
    inputValue,
    highlightedIndex,
    isCreating,
    inputRef,
    listRef,
    containerRef,
    isMobile,
    baseId,
    inputId,
    listboxId,
    labelId,
    statusId,
    selectedValues,
    atLimit,
    filteredOptions,
    canCreate,
    displayValue,
    handleInputChange,
    handleSelect,
    handleCreate,
    handleClear,
    handleKeyDown,
    handleInputFocus,
    openAndFocus,
    settleTypedText,
    discardTypedText,
  };
}

export type AutocompleteState = ReturnType<typeof useAutocomplete>;

export interface AutocompleteViewProps {
  props: ResolvedAutocompleteProps;
  state: AutocompleteState;
}
